/**
 * Local persistence for an in-progress *public* mock test.
 *
 * Public mock tests have NO per-answer server call (unlike the assigned/private
 * exam flow). For guests in particular, every answer lives only on the client
 * until the single `submit` call at the end. To survive a page refresh / tab
 * close mid-test we mirror the answer map to localStorage, keyed by exam + mode
 * + the selected parts (so "full" and a "part" run of the same exam never clash).
 *
 * Storage shape:
 *   {
 *     v: 1,
 *     examId, mode, parts,
 *     answers: { [questionId]: selectedOption },
 *     startedAt: ISO string,
 *     readingRemainingSeconds: number | null,
 *     savedAt: ISO string
 *   }
 *
 * All operations are best-effort — localStorage can throw in private mode or
 * when the quota is exceeded; we swallow those errors because losing a draft is
 * strictly less bad than crashing the test page.
 */

const PREFIX = "toeic.publicMockDraft.v1.";
const SCHEMA_VERSION = 1;

const normalizeParts = (parts) =>
  Array.isArray(parts) ? [...parts].sort((a, b) => a - b).join("-") : "";

export const draftKey = (examId, mode, parts) =>
  `${PREFIX}${examId}.${mode}.${normalizeParts(parts)}`;

export const savePublicMockDraft = (
  examId,
  mode,
  parts,
  { answers, startedAt, readingRemainingSeconds }
) => {
  if (!examId || !mode) return;
  try {
    const payload = {
      v: SCHEMA_VERSION,
      examId,
      mode,
      parts: Array.isArray(parts) ? parts : [],
      answers: answers || {},
      startedAt: startedAt || null,
      readingRemainingSeconds:
        Number.isFinite(readingRemainingSeconds)
          ? Math.max(0, Math.floor(readingRemainingSeconds))
          : null,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(draftKey(examId, mode, parts), JSON.stringify(payload));
  } catch (_) {
    // ignored — private mode / quota
  }
};

export const loadPublicMockDraft = (examId, mode, parts) => {
  if (!examId || !mode) return null;
  try {
    const raw = localStorage.getItem(draftKey(examId, mode, parts));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== SCHEMA_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

export const clearPublicMockDraft = (examId, mode, parts) => {
  if (!examId || !mode) return;
  try {
    localStorage.removeItem(draftKey(examId, mode, parts));
  } catch (_) {
    // ignored
  }
};

/**
 * Remove drafts older than `maxAgeMs` (default 7 days). Called on mount to keep
 * localStorage tidy if a user starts many public tests and never finishes them.
 */
export const sweepStalePublicMockDrafts = (
  maxAgeMs = 7 * 24 * 60 * 60 * 1000
) => {
  try {
    const now = Date.now();
    const stale = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      try {
        const parsed = JSON.parse(localStorage.getItem(k));
        const savedAt = parsed?.savedAt ? Date.parse(parsed.savedAt) : 0;
        if (!savedAt || now - savedAt > maxAgeMs) stale.push(k);
      } catch (_) {
        stale.push(k);
      }
    }
    stale.forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    // ignored
  }
};
